import { useTheme } from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";

import Design from "@/Components/Icons/Design";
import Hypothesis from "@/Components/Icons/Hypothesis";
import Measurement from "@/Components/Icons/Measurement";
import Timeframe from "@/Components/Icons/Timeframe";
import { Box } from "@/Components/v2/atoms/Box";
import { Divider } from "@/Components/v2/atoms/Divider";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Typography } from "@/Components/v2/atoms/Typography";

import { TransformedExperiment } from "../api/getTestingRecommendations";
import { getRiskLevelColor, getTimeframeLabelColor } from "../utils/colorMapping";
import { useNavigateToInvestmentLevels } from "../utils/investmentLevelsNavigation";

interface ExperimentDetailsProps {
  experiment: TransformedExperiment;
}

export const ExperimentDetails = ({ experiment }: ExperimentDetailsProps) => {
  const theme = useTheme();
  const { navigateToInvestmentLevels } = useNavigateToInvestmentLevels();

  const sections = [
    { icon: <Design />, label: "DESIGN", content: experiment.details.design },
    { icon: <Hypothesis />, label: "HYPOTHESIS", content: experiment.details.hypothesis },
    { icon: <Measurement />, label: "MEASUREMENT", content: experiment.details.measurement },
    { icon: <Timeframe />, label: "TIMEFRAME", content: experiment.details.timeframe },
  ];

  // Get dynamic colors based on theme mode
  const riskLevelColor = getRiskLevelColor(experiment.details.riskLevel, theme.palette.mode);
  const timeframeLabelColor = getTimeframeLabelColor(
    experiment.details.timeframeLabel,
    theme.palette.mode
  );
  // Use Low risk level color for potential increase indicator
  const potentialIncreaseColor = getRiskLevelColor("Low", theme.palette.mode);

  return (
    <Box sx={{ p: 3, height: "100%" }}>
      <Typography variant="h6" component="h4" fontWeight={600} mb={2}>
        {experiment.title}
      </Typography>

      <Typography variant="body2" mb={3} color="text.secondary">
        {experiment.details.description}
      </Typography>

      <Divider />

      <Box py={2}>
        <Stack direction="row" spacing={6} sx={{ width: "100%" }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ color: potentialIncreaseColor }}>
              {experiment.details.potentialIncreaseAmount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {experiment.details.potentialIncreaseText}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ color: riskLevelColor }}>
              {experiment.details.riskLevel}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Risk Level
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ color: timeframeLabelColor }}>
              {experiment.details.timeframeLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Timeframe
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Divider />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          gap: 2,
          columnGap: 4,
          alignItems: "start",
          mt: 3,
        }}
      >
        {sections.map((section, index) => (
          <React.Fragment key={index}>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box>{section.icon}</Box>
              <Typography variant="body2" fontWeight={600}>
                {section.label}
              </Typography>
            </Box>
            <Box>
              {Array.isArray(section.content) ? (
                <Stack direction="column" spacing={1}>
                  {section.content.map((item, itemIndex) => (
                    <Typography key={itemIndex} variant="body2" color="text.secondary">
                      {item}
                    </Typography>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {section.content}
                </Typography>
              )}
            </Box>
          </React.Fragment>
        ))}
      </Box>
      <Box sx={{ mt: 3 }}>
        <Typography
          sx={{
            fontWeight: 400,
            fontStyle: "italic",
            fontSize: "12px",
          }}
        >
          MAITE is under development and may provide unexpected results. Always verify information
          before making important decisions.{" "}
          <Link
            to="/investment-levels"
            onClick={() => navigateToInvestmentLevels(experiment)}
            style={{ color: theme.palette.primary.main, textDecoration: "underline" }}
          >
            View this channel in Investment Levels
          </Link>
          .
        </Typography>
      </Box>
    </Box>
  );
};
